/** Plan keys and limits (FULL_SCOPE_DOCUMENT M20). `null` = custom/contract. Prices are not
 * constants: they come from the per-currency price books (`/v1/billing/plans`, DEC-042). */
export const PLAN_KEYS = ["starter", "standard", "pro", "institution"] as const;
export type PlanKey = (typeof PLAN_KEYS)[number];

export interface PlanLimits {
  readonly messageCredits: number | null;
  readonly agents: number | null;
  readonly storageMb: number | null;
  readonly seats: number | null;
  readonly customRoles: boolean;
}

export const PLAN_LIMITS: Readonly<Record<PlanKey, PlanLimits>> = {
  starter: { messageCredits: 2000, agents: 1, storageMb: 20, seats: 2, customRoles: false },
  standard: { messageCredits: 10000, agents: 5, storageMb: 100, seats: 5, customRoles: false },
  pro: { messageCredits: 40000, agents: 15, storageMb: 1024, seats: 15, customRoles: true },
  institution: { messageCredits: null, agents: null, storageMb: null, seats: null, customRoles: true },
};

/** Plan features a lower plan can buy as a recurring add-on (PD-11, DEC-151): Billing lists one only while the
 *  catalogue sells it (Admin > Add-ons switches it on) on the current plan, which does not already have it.
 *  The two Secure launch add-ons exclude each other, lowest first: Lite (the launch) then Plus (the launch and customer facts). */
export const FEATURE_ADDON_KEYS = ["secure_launch_lite", "secure_launch"] as const;
export type FeatureAddonKey = (typeof FEATURE_ADDON_KEYS)[number];

/** Months charged for a year on every price book (backend `ANNUAL_MONTHS_CHARGED`): annual = 10 x monthly. */
export const ANNUAL_MONTHS_CHARGED = 10 as const;

/** The add-on that carries `secure_launch_facts`; Institution has the same in its plan. */
export const SECURE_LAUNCH_PLUS_ADDON = "secure_launch" as const;
export const SECURE_LAUNCH_LITE_ADDON = "secure_launch_lite" as const;

/** Launches (mints) a Lite workspace may make in a calendar month (Asia/Manila); Plus and Institution: unlimited. */
export const SECURE_LAUNCH_LITE_MONTHLY_LAUNCHES = 2000 as const;

/** Whether `plan` is `minimum` or higher (an unknown plan never qualifies). */
export function planAtLeast(plan: string | undefined, minimum: PlanKey): boolean {
  const index = PLAN_KEYS.findIndex((key) => key === plan);
  return index >= 0 && index >= PLAN_KEYS.indexOf(minimum);
}
